import * as React from 'react';

interface ISrcSet {
  size: '1x' | '2x' | '3x';
  src: string;
}

interface IImageConfig {
  name: string;
  src: string;
  srcSet?: ISrcSet[];
}

export type VALID_IMAGES =
  | '404-large'
  | '404-small'
  | '404'
  | '503-large'
  | '503-small'
  | '503'
  | 'avant-sig'
  | 'bank-statement'
  | 'pay-statement'
  | 'voided-check'
  | 'checklist-complete'
  | 'card-graphic'
  | 'card-bank'
  | 'paperless'
  | 'creditcard-autopay-peace'
  | 'creditcard-autopay-set'
  | 'creditcard-autopay-stay'
  | 'creditcard-avant-cloud'
  | 'creditcard-avant'
  | 'creditcard-fraud'
  | 'creditcard-getting-started'
  | 'email-sent'
  | 'getting-started'
  | 'loading'
  | 'maintenance-large'
  | 'maintenance-small'
  | 'netverify'
  | 'noaction'
  | 'notice-decline'
  | 'offer-guide'
  | 'rates-arrow'
  | 'regions-plus-avant'
  | 'snapshot-process'
  | 'thumb-help-large'
  | 'thumb-help-regions-large'
  | 'thumb-help-regions-small'
  | 'thumb-help-small'
  | 'thumb-seal-large'
  | 'thumb-seal-small'
  | 'upload-browse'
  | 'upload-tap'
  | 'id-front'
  | 'passport-front'
  | 'id-back'
  | 'selfie'
  | 'bbb-logo-small'
  | 'bbb-logo-large'
  | 'blank-check-routing'
  | 'blank-check-account'
  | 'mobile';

export const SVG_INFO_MAP = new Map<VALID_IMAGES, React.FunctionComponent<React.SVGAttributes<SVGElement>>>([
  ['404', React.lazy(() => import(`../../images/dec/Dec404`))],
  ['503', React.lazy(() => import(`../../images/dec/Dec503`))],
  ['avant-sig', React.lazy(() => import(`../../images/dec/DecAvantSig`))],
  ['checklist-complete', React.lazy(() => import(`../../images/dec/DecChecklistComplete`))],
  ['creditcard-autopay-peace', React.lazy(() => import(`../../images/dec/DecCreditcardAutopayPeace`))],
  ['creditcard-autopay-set', React.lazy(() => import(`../../images/dec/DecCreditcardAutopaySet`))],
  ['creditcard-autopay-stay', React.lazy(() => import(`../../images/dec/DecCreditcardAutopayStay`))],
  ['creditcard-avant-cloud', React.lazy(() => import(`../../images/dec/DecCreditcardAvantCloud`))],
  ['creditcard-avant', React.lazy(() => import(`../../images/dec/DecCreditcardAvant`))],
  ['creditcard-fraud', React.lazy(() => import(`../../images/dec/DecCreditcardFraud`))],
  ['creditcard-getting-started', React.lazy(() => import(`../../images/dec/DecCreditcardGettingStarted`))],
  ['email-sent', React.lazy(() => import(`../../images/dec/DecEmailSent`))],
  ['getting-started', React.lazy(() => import(`../../images/dec/DecGettingStarted`))],
  ['loading', React.lazy(() => import(`../../images/dec/DecLoading`))],
  ['noaction', React.lazy(() => import(`../../images/dec/DecNoaction`))],
  ['notice-decline', React.lazy(() => import(`../../images/dec/DecNoticeDecline`))],
  ['offer-guide', React.lazy(() => import(`../../images/dec/DecOfferGuide`))],
  ['regions-plus-avant', React.lazy(() => import(`../../images/dec/DecRegionsPlusAvant`))],
  ['upload-browse', React.lazy(() => import(`../../images/dec/DecUploadBrowse`))],
  ['upload-tap', React.lazy(() => import(`../../images/dec/DecUploadTap`))],
  ['id-front', React.lazy(() => import(`../../images/dec/DecIdFront`))],
  ['id-back', React.lazy(() => import(`../../images/dec/DecIdBack`))],
  ['passport-front', React.lazy(() => import(`../../images/dec/DecPassportFront`))],
  ['selfie', React.lazy(() => import(`../../images/dec/DecSelfie`))],
  ['mobile', React.lazy(() => import(`../../images/dec/DecMobile`))],
]);

const IMAGE_INFO: IImageConfig[] = [
  { name: '404-large', src: 'dec-404-large.png' },
  { name: '404-small', src: 'dec-404-small.png' },
  { name: '503-large', src: 'dec-503-large.jpg' },
  { name: '503-small', src: 'dec-503-small.jpg' },
  {
    name: 'card-bank',
    src: 'dec-card-bank.png',
    srcSet: [
      { size: '1x', src: 'dec-card-bank.png' },
      { size: '2x', src: 'dec-card-bank@2x.png' },
      { size: '3x', src: 'dec-card-bank@3x.png' }
    ]
  },
  {
    name: 'card-graphic',
    src: 'dec-card-graphic.png',
    srcSet: [
      { size: '1x', src: 'dec-card-graphic.png' },
      { size: '2x', src: 'dec-card-graphic@2x.png' },
      { size: '3x', src: 'dec-card-graphic@3x.png' }
    ]
  },
  {
    name: 'paperless',
    src: 'dec-paperless.png',
    srcSet: [
      { size: '1x', src: 'dec-paperless.png' },
      { size: '2x', src: 'dec-paperless@2x.png' },
      { size: '3x', src: 'dec-paperless@3x.png' }
    ]
  },
  {
    name: 'bank-statement',
    src: 'dec-bank-statement.png',
    srcSet: [
      { size: '1x', src: 'dec-bank-statement.png' },
      { size: '2x', src: 'dec-bank-statement@2x.png' }
    ]
  },
  {
    name: 'pay-statement',
    src: 'dec-pay-statement.png',
    srcSet: [
      { size: '1x', src: 'dec-pay-statement.png' },
      { size: '2x', src: 'dec-pay-statement@2x.png' }
    ]
  },
  {
    name: 'voided-check',
    src: 'dec-voided-check.png',
    srcSet: [
      { size: '1x', src: 'dec-voided-check.png' },
      { size: '2x', src: 'dec-voided-check@2x.png' }
    ]
  },
  { name: 'maintenance-large', src: 'dec-maintenance-large.jpg' },
  { name: 'maintenance-small', src: 'dec-maintenance-small.jpg' },
  {
    name: 'netverify',
    src: 'dec-netverify-jumio.png',
    srcSet: [
      { size: '1x', src: 'dec-netverify-jumio.png' },
      { size: '2x', src: 'dec-netverify-jumio@2x.png' }
    ]
  },
  {
    name: 'snapshot-process',
    src: 'dec-snapshot-process.jpg',
    srcSet: [
      { size: '1x', src: 'dec-snapshot-process.jpg' },
      { size: '2x', src: 'dec-snapshot-process@2x.jpg' }
    ]
  },
  {
    name: 'blank-check-routing',
    src: 'dec-check-blank-routing.png',
    srcSet: [
      { size: '1x', src: 'dec-check-blank-routing.png' },
      { size: '2x', src: 'dec-check-blank-routing@2x.png' }
    ]
  },
  {
    name: 'blank-check-account',
    src: 'dec-check-blank-account.png',
    srcSet: [
      { size: '1x', src: 'dec-check-blank-account.png' },
      { size: '2x', src: 'dec-check-blank-account@2x.png' }
    ]
  },
  { name: 'thumb-help-large', src: 'dec-thumb-help-large.png' },
  { name: 'thumb-help-regions-large', src: 'dec-thumb-help-regions-large.png' },
  { name: 'thumb-help-regions-small', src: 'dec-thumb-help-regions-small.png' },
  { name: 'thumb-help-small', src: 'dec-thumb-help-small.png' },
  { name: 'thumb-seal-large', src: 'dec-thumb-seal-large.png' },
  { name: 'thumb-seal-small', src: 'dec-thumb-seal-small.png' },
  { name: 'bbb-logo-small', src: 'dec-bbb-logo-small.png' },
  { name: 'bbb-logo-large', src: 'dec-bbb-logo-large.png' },
];

interface ISourceInfo {
  src: string;
  srcSet?: string;
}

const requireDec: (image: string) => string = image =>
  // tslint:disable-next-line non-literal-require
  require(`../../../src/images/dec/${image}`);

const buildSrcSet: (
  srcSet: IImageConfig['srcSet']
) => string | undefined = srcSet => {
  if (!srcSet) {
    return undefined;
  }

  return srcSet
    .map(({ size, src }) => `${requireDec(src)} ${size}`)
    .join(', ');
};

export const IMAGE_INFO_MAP: Map<VALID_IMAGES, ISourceInfo> = new Map<
  VALID_IMAGES,
  ISourceInfo
>(
  IMAGE_INFO.map(
    ({ name, src, srcSet }) =>
      [name, { src: requireDec(src), srcSet: buildSrcSet(srcSet) }] as [
        VALID_IMAGES,
        ISourceInfo
      ]
  )
);

export interface IDecorativeElementProps {
  image: VALID_IMAGES;
}

export default class DecorativeElement extends React.PureComponent<
  React.ImgHTMLAttributes<HTMLImageElement> & IDecorativeElementProps
> {
  public render () {
    const { image, ...props } = this.props;

    if (SVG_INFO_MAP.has(image)) {
      const Icon = SVG_INFO_MAP.get(image);

      if (!Icon) { throw new Error('Image Missing'); }

      return (
        <React.Suspense fallback={null}>
          <Icon
            focusable='false'
            {...props as React.FC<IDecorativeElementProps & React.SVGAttributes<SVGElement>>}
            aria-hidden={true}
          />
        </React.Suspense>
      );
    }

    const imgProps: ISourceInfo | undefined = IMAGE_INFO_MAP.get(image);

    if (!imgProps) { throw new Error('Image Missing'); }

    return <img aria-hidden={true} {...props} {...imgProps} />;
  }
}
